// Custom Hook for Performance Type Color Management

import { useState, useEffect, useCallback } from 'react';
import { 
  PerformanceTypeColor, 
  CustomColorScheme, 
  ColorSchemeExport, 
  ColorValidationResult,
  UsePerformanceTypeColorsReturn 
} from '../types/colorTypes';
import { 
  saveCustomColors, 
  loadCustomColors, 
  clearCustomColors, 
  exportColorScheme, 
  importColorScheme,
  downloadColorScheme 
} from '../utils/colorStorage';
import { 
  validateColorAccessibility, 
  generateAccessibleTextColor, 
  generateAccessibleBorderColor 
} from '../utils/colorUtilities';
import { clearCustomColorsCache } from '../utils/performanceTypeColors';

export const usePerformanceTypeColors = (): UsePerformanceTypeColorsReturn => {
  const [customColors, setCustomColors] = useState<CustomColorScheme>({});

  // Load custom colors on mount
  useEffect(() => {
    const loaded = loadCustomColors();
    setCustomColors(loaded);
  }, []);

  // Set custom color for a performance type
  const setCustomColor = useCallback((performanceType: string, color: PerformanceTypeColor) => {
    const newColors = {
      ...customColors,
      [performanceType]: color
    };
    
    setCustomColors(newColors);
    saveCustomColors(newColors);
    clearCustomColorsCache(); // Clear cache to force refresh
    
    // Trigger a window storage event to notify other components
    window.dispatchEvent(new Event('storage'));
  }, [customColors]);

  // Set custom color by background color (auto-generate text and border)
  const setCustomColorByBackground = useCallback((performanceType: string, backgroundColor: string, name?: string) => {
    const textColor = generateAccessibleTextColor(backgroundColor);
    const borderColor = generateAccessibleBorderColor(backgroundColor);
    
    const color: PerformanceTypeColor = {
      backgroundColor,
      textColor,
      borderColor,
      name: name || `Custom ${performanceType}`
    };
    
    setCustomColor(performanceType, color);
  }, [setCustomColor]);

  // Reset to default colors
  const resetToDefaults = useCallback(() => {
    setCustomColors({});
    clearCustomColors();
    clearCustomColorsCache();
  }, []);

  // Reset specific performance type to default
  const resetPerformanceTypeToDefault = useCallback((performanceType: string) => {
    const newColors = { ...customColors };
    delete newColors[performanceType];
    
    setCustomColors(newColors);
    saveCustomColors(newColors);
    clearCustomColorsCache();
  }, [customColors]);

  // Export color scheme
  const exportColorSchemeCallback = useCallback((name: string, description?: string): ColorSchemeExport => {
    return exportColorScheme(customColors, name, description);
  }, [customColors]);

  // Download color scheme as JSON file
  const downloadColorSchemeCallback = useCallback((name: string, description?: string) => {
    const scheme = exportColorSchemeCallback(name, description);
    downloadColorScheme(scheme);
  }, [exportColorSchemeCallback]);

  // Import color scheme
  const importColorSchemeCallback = useCallback((scheme: ColorSchemeExport): boolean => {
    try {
      setCustomColors(scheme.colors);
      saveCustomColors(scheme.colors);
      clearCustomColorsCache();
      return true;
    } catch (error) {
      console.error('Failed to import color scheme:', error);
      return false;
    }
  }, []);

  // Import color scheme from JSON string
  const importColorSchemeFromJSON = useCallback((jsonString: string): boolean => {
    const scheme = importColorScheme(jsonString);
    if (scheme) {
      return importColorSchemeCallback(scheme);
    }
    return false;
  }, [importColorSchemeCallback]);

  // Validate color scheme
  const validateColorScheme = useCallback((scheme: CustomColorScheme): ColorValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    Object.entries(scheme).forEach(([performanceType, color]) => {
      const validation = validateColorAccessibility(
        color.backgroundColor,
        color.textColor,
        color.borderColor
      );
      
      if (!validation.isValid) {
        errors.push(`${performanceType}: ${validation.errors.join(', ')}`);
      }
      
      if (validation.warnings.length > 0) {
        warnings.push(`${performanceType}: ${validation.warnings.join(', ')}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }, []);

  // Check if a performance type has custom colors
  const hasCustomColor = useCallback((performanceType: string): boolean => {
    return performanceType in customColors;
  }, [customColors]);

  // Get all performance types with custom colors
  const getCustomizedPerformanceTypes = useCallback((): string[] => {
    return Object.keys(customColors);
  }, [customColors]);

  return {
    customColors,
    setCustomColor,
    setCustomColorByBackground,
    resetToDefaults,
    resetPerformanceTypeToDefault,
    exportColorScheme: exportColorSchemeCallback,
    downloadColorScheme: downloadColorSchemeCallback,
    importColorScheme: importColorSchemeCallback,
    importColorSchemeFromJSON,
    validateColorScheme,
    hasCustomColor,
    getCustomizedPerformanceTypes
  };
};